/**
 * QuickActions — pill action buttons under the greeting.
 *
 * Per Figma (node 562:22093): Upload Receipt is the primary action;
 * Request New Card / Submit Reimbursements / Resolve Compliance are
 * secondary. The first three open their respective drawers (state is
 * owned by the Dashboard page and passed in via handlers).
 *
 * `onActivateCard` is optional and only surfaces an "Activate Card" pill
 * when supplied — the Dashboard passes it only while the employee has an
 * issued (not-yet-activated) card, giving a persistent entry point to the
 * activation screen that survives dismissing the ActivateCardBanner.
 */
import React from "react";
import { HugeiconsIcon, IconSvgElement } from "@hugeicons/react";
import {
  CashbackIcon,
  CreditCardAcceptIcon,
  CreditCardAddIcon,
  Note03Icon,
  Upload01Icon,
} from "@hugeicons-pro/core-stroke-rounded";
import { Inline } from "components/layout/reusableLayouts";
import { useTranslation } from "react-i18next";
import { Button } from "@mui/material";

export interface QuickActionsProps {
  onUploadReceipt: () => void;
  onRequestCard: () => void;
  /** Omitted when the tenant is not entitled to out-of-pocket reimbursements. */
  onSubmitReimbursement?: () => void;
  onActivateCard?: () => void;
  onResolveCompliance?: () => void;
}

const QuickActions: React.FC<QuickActionsProps> = ({
  onUploadReceipt,
  onRequestCard,
  onSubmitReimbursement,
  onActivateCard,
  onResolveCompliance,
}) => {
  const { t } = useTranslation();

  const btns: {
    icon: IconSvgElement;
    label: string;
    variant: "primary-filled" | "default-outlined";
    onClick: () => void;
  }[] = [
    {
      icon: Upload01Icon,
      label: t("Upload Receipt"),
      variant: "primary-filled",
      onClick: onUploadReceipt,
    },
    {
      icon: CreditCardAddIcon,
      label: t("Request New Card"),
      variant: "default-outlined",
      onClick: onRequestCard,
    },
    ...(onActivateCard
      ? [
          {
            icon: CreditCardAcceptIcon,
            label: t("Activate Card"),
            variant: "default-outlined" as const,
            onClick: onActivateCard,
          },
        ]
      : []),
    ...(onSubmitReimbursement
      ? [
          {
            icon: CashbackIcon,
            label: t("Submit Reimbursements"),
            variant: "default-outlined" as const,
            onClick: onSubmitReimbursement,
          },
        ]
      : []),
    ...(onResolveCompliance
      ? [
          {
            icon: Note03Icon,
            label: t("Resolve Compliance"),
            variant: "default-outlined" as const,
            onClick: onResolveCompliance,
          },
        ]
      : []),
  ];

  return (
    <Inline gap="var(--space-5)">
      {btns.map((btn, idx) => (
        <Button
          key={`${btn.label}-${idx}`}
          variant={btn.variant}
          startIcon={<HugeiconsIcon icon={btn.icon} size={16} />}
          onClick={btn.onClick}
        >
          {btn.label}
        </Button>
      ))}
    </Inline>
  );
};

export default QuickActions;
